import React from 'react';
import { useSelector } from 'react-redux';

import { AICoachGoalItem } from 'api/LoanOfferApi';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';
import { ReactComponent as AIIcon } from 'images/ai-stars-icon.svg';

import { GetApplicationData, HardOfferData } from 'handlers/applicationData';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { selectIsShiftRequested } from 'selectors/getAvailableShifts';

import { hasSelectedAICoachGoals } from 'utils/aiCoachUtils';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { formatMonthDay } from 'utils/dateUtils';

enum NextStepButtonText {
  SeeYourOffer = 'See Your Offer',
  GetYourOffer = 'Get Your Offer',
  SaveTime = 'Save Time Now',
  GetFunds = 'Get Funds Now',
}

export type ActionCardProps = {
  title: string;
  description?: React.ReactNode;
  buttonText?: NextStepButtonText | React.ReactNode;
  estimatedDuration?: string;
  route?: RoutePath;
  chip?: React.ReactNode;
  isLoading?: boolean;
};

const reviewGoalsButtonContent = (
  <>
    <AIIcon /> <span>Review goals</span>
  </>
);

export const getCardByLoanApplicationStatus = (
  application: GetApplicationData,
  aiCoachGoals: AICoachGoalItem[],
  isLoadingGoals?: boolean,
): ActionCardProps | null => {
  const { status, borrowerFirstName } = application;
  const hardOffer = application.hardOffer as HardOfferData;
  const moneySaved = hardOffer?.offerSummary?.moneySaved;
  const monthsSaved = hardOffer?.offerSummary?.monthsSaved;
  const loanAmount = hardOffer?.offerSummary?.loanAmount || application.loanAmount;

  switch (status) {
    case ApplicationStatusName.ManualReview:
      if (application.crsNoFileReturned) {
        return {
          title: 'Help us match your credit profile',
          description: (
            <>
              We need a few more details to find your credit file—
              <strong>takes just a minute.</strong>
            </>
          ),
          buttonText: 'Continue application',
          estimatedDuration: '2 min',
          route: RoutePath.ExtendedIdentityCheck,
        };
      }
      if (application.crsSmfaVerificationRequired) {
        return {
          title: 'Verify your identity',
          description: (
            <>
              The bureau sent a text with a verification link. Complete it, then come back—
              <strong>takes just a minute.</strong>
            </>
          ),
          buttonText: 'Continue application',
          estimatedDuration: '1 min',
          route: RoutePath.BureauVerification,
        };
      }
      if (application.frozenCreditScore) {
        return {
          title: 'Unfreeze your credit',
          description: (
            <>
              Your Equifax credit file is frozen. Unfreeze it to continue your application—
              <strong>takes just a minute.</strong>
            </>
          ),
          buttonText: 'Unfreeze credit',
          estimatedDuration: '1 min',
          route: RoutePath.UnfreezeCreditScore,
        };
      }
      return {
        title: 'Take action on your offer',
        description: (
          <>
            We need your help to finish your offer. Reach out today—<strong>takes just a minute.</strong>
          </>
        ),
        buttonText: NextStepButtonText.GetYourOffer,
        estimatedDuration: '1 min',
        route: RoutePath.OfferStatus,
      };

    case ApplicationStatusName.Verification:
      return {
        title: 'Save money on interest',
        description: moneySaved
          ? `Finish setting up your account to unlock ${formatMonetaryAmount(moneySaved)} in savings.`
          : 'Finish setting up your account to unlock your savings.',
        buttonText: NextStepButtonText.SeeYourOffer,
        estimatedDuration: '5 min',
        route: RoutePath.VerificationSteps,
      };

    case ApplicationStatusName.FinalizeLoan:
      return {
        title: 'Which debts will you pay off?',
        description:
          monthsSaved && monthsSaved > 0
            ? `${borrowerFirstName}, pay off debt ${monthsSaved} months faster.`
            : `You're in control—pick the balances to pay off and move forward with your ${formatMonetaryAmount(
                loanAmount,
              )} plan.`,
        buttonText: NextStepButtonText.SaveTime,
        estimatedDuration: '5 min',
        route: RoutePath.SelectDebts,
      };

    case ApplicationStatusName.SetUpDeduction:
      return {
        title: 'Get your savings offer',
        description: 'Set up your repayment so payments are always on time.',
        buttonText: NextStepButtonText.SeeYourOffer,
        estimatedDuration: '5 min',
        route: RoutePath.RepaymentMethod,
      };

    case ApplicationStatusName.SigningAgreement:
      return {
        title: `Get your ${formatMonetaryAmount(loanAmount)} loan`,
        description: `Offer valid through ${formatMonthDay(new Date(application.offerExpirationDate))}.`,
        buttonText: NextStepButtonText.GetFunds,
        estimatedDuration: '5 min',
        route: RoutePath.DocuSignSignature,
      };

    case ApplicationStatusName.ManualVerify:
    case ApplicationStatusName.FinalReview:
      return {
        title: 'Your loan is under review',
        description: `We'll get back to you soon!`,
      };

    case ApplicationStatusName.AwaitingFunding:
    case ApplicationStatusName.Funded:
      if (hasSelectedAICoachGoals(aiCoachGoals)) return null;
      return {
        title: 'Nice work taking care of your debt',
        description: "Let's review your finances and identify the right financial goal to focus on next.",
        buttonText: reviewGoalsButtonContent,
        route: RoutePath.AICoachGoals,
        isLoading: isLoadingGoals,
      };
    default:
      return null;
  }
};

export const mapAppStatusToActionCardProps = (
  application: GetApplicationData,
  aiCoachGoals: AICoachGoalItem[],
): ActionCardProps | null => {
  const { isLoading: isLoadingGoals } = useSelector(getLoanGoal);
  const isShiftRequested = useSelector(selectIsShiftRequested);

  if (isShiftRequested) return { title: 'Great! We have submitted your request' };

  if (application.canReapply) {
    return {
      title: 'You have a loan you can apply for',
      description: 'Confirm your details to continue your application.',
      buttonText: 'Apply now',
      route: RoutePath.ConfirmReapplyDetails,
    };
  }

  return getCardByLoanApplicationStatus(application, aiCoachGoals, isLoadingGoals);
};
